import { useNavigate } from '@fukuri-admins/hooks/common';
import { Loader } from '@fukuri-admins/ui/common';
import {
  getToken,
  removeStressAssessmentRole,
  removeThreeLetterCode,
  removeToken,
  setClientName,
  setEmail,
  setThreeLetterCode,
} from '@fukuri-admins/utils/common/cookies';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { mutate } from 'swr';
import { Client } from '../../types/model/client';
import { ROUTES } from '../../utils/routes';

export type UserInfo = {
  client_admin_user: {
    id: string;
    full_name: string;
    email: string;
    external_client_admin_user_id: string | null;
    client: Client;
  };
  three_letter_code: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  signIn: (userInfo: UserInfo) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // NOTE: 初期化時にトークンの存在確認
    const token = getToken();
    if (token) {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const signIn = (user: UserInfo) => {
    setIsAuthenticated(true);
    setEmail(user.client_admin_user.email);
    setClientName(user.client_admin_user.client.name);
    setThreeLetterCode(user.three_letter_code);
    navigate(ROUTES.ROOT);
  };

  const signOut = () => {
    removeToken();
    removeThreeLetterCode();
    removeStressAssessmentRole();
    setIsAuthenticated(false);
    navigate(ROUTES.LOGIN);
    mutate(() => true, undefined, { revalidate: false });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
